import { useEffect, useState } from "react";
import {
  fetchNevisignDocuments,
  type FetchDocumentsResult,
  type NevisignDocumentListItem,
} from "@nevisign/reader-core";
import { ArrowRight, RefreshCw, Search } from "lucide-react";
import { ensureFreshAccessToken } from "../lib/platform-auth";
import { requestAuthForAction } from "../lib/auth-required";
import { useAuthStore } from "../stores/auth-store";

type Props = {
  apiBase: string;
  onOpenDocument: (doc: NevisignDocumentListItem) => void;
  openingId?: string | null;
  authenticated?: boolean;
  onBack?: () => void;
};

export function NevisignDocsPanel({
  apiBase,
  onOpenDocument,
  openingId,
  authenticated = true,
  onBack,
}: Props) {
  const [result, setResult] = useState<FetchDocumentsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const currentUserId = useAuthStore((s) => s.user?.id);

  const load = async (q?: string) => {
    if (!authenticated) return;
    setLoading(true);
    await ensureFreshAccessToken();
    const res = await fetchNevisignDocuments(apiBase, {
      search: (q ?? search).trim() || undefined,
      pageSize: 100,
    });
    setResult(res);
    setLoading(false);
    if (
      res.error &&
      (res.error.includes("منقضی") || res.error.includes("وارد"))
    ) {
      requestAuthForAction(() => void load(q));
    }
  };

  useEffect(() => {
    if (!authenticated) {
      setResult(null);
      return;
    }
    void load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when base changes
  }, [apiBase, authenticated]);

  return (
    <div className={`docs-list-page consumer-shell${onBack ? "" : " docs-list-side"}`}>
      {onBack ? (
        <header className="list-page-hero">
          <button type="button" className="text-back" onClick={onBack}>
            <ArrowRight size={16} />
            بازگشت
          </button>
          <div className="list-page-hero-copy">
            <span className="consumer-kicker">اسناد نویساین</span>
            <h1>قراردادها و اسناد ابری</h1>
            <p>برای ادامه ویرایش، بررسی یا امضا یکی از اسناد سازمان را باز کنید.</p>
          </div>
        </header>
      ) : (
        <div className="dashboard-section-head">
          <div className="dashboard-section-title">
            <img src="/brand/nevisign-logo.svg" alt="" className="brand-mark" />
            <div>
              <h2>اسناد نویساین</h2>
              <p>فهرست با حساب شما همگام می‌شود.</p>
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            disabled={loading}
            onClick={() => void load()}
            aria-label="بروزرسانی اسناد"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      )}

      {onBack ? (
        <div className="dashboard-section">
          <div className="dashboard-section-head">
            <div className="dashboard-section-title">
              <img src="/brand/nevisign-logo.svg" alt="" className="brand-mark" />
              <div>
                <h2>اتصال به فضای ابری</h2>
                <p>فهرست با همان حساب نویساین شما همگام می‌شود.</p>
              </div>
            </div>
            <button
              type="button"
              className="icon-btn"
              disabled={loading}
              onClick={() => void load()}
            >
              <RefreshCw size={14} />
              بروزرسانی
            </button>
          </div>
        </div>
      ) : null}

      <form
        className="list-search-card"
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
      >
        <Search size={16} aria-hidden />
        <input
          type="search"
          dir="rtl"
          placeholder="جستجو در عنوان یا موضوع…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="جستجوی اسناد"
        />
        <button type="submit" className="icon-btn compact" disabled={loading || !authenticated}>
          جستجو
        </button>
      </form>

      {!authenticated ? (
        <div className="dashboard-auth-prompt">
          <p>برای مشاهده اسناد ابری وارد حساب نویساین شوید.</p>
        </div>
      ) : null}

      {result?.error && (
        <div className="status-banner" role="alert">
          {result.error}
        </div>
      )}
      {result?.source === "api" && (
        <div className="status-banner ok">
          {result.data.total.toLocaleString("fa-IR")} سند از سازمان شما
        </div>
      )}
      {result?.source === "empty" && !result.error && (
        <div className="status-banner ok">
          سندی در این سازمان نیست. از پنل نویساین قرارداد بسازید یا همین‌جا فایل باز
          کنید و ذخیره ابری بزنید.
        </div>
      )}

      {loading && <p className="settings-hint">در حال دریافت…</p>}

      <div className="docs-list-stack">
        {result?.data.documents.map((doc) => {
          const creatorName = doc.created_by_name?.trim() || "";
          const showCreator =
            Boolean(creatorName) &&
            Boolean(currentUserId) &&
            doc.created_by_user_id !== currentUserId;
          return (
          <button
            key={doc.id}
            type="button"
            className="doc-list-item"
            disabled={Boolean(openingId)}
            onClick={() => onOpenDocument(doc)}
          >
            <div className="doc-list-title">
              {openingId === doc.id ? "در حال باز کردن…" : doc.title}
            </div>
            <div className="doc-list-meta">
              <span className="badge">{doc.kind_label}</span>
              <span>{doc.status_label}</span>
              {showCreator ? <span>ایجادکننده: {creatorName}</span> : null}
              {doc.parties_summary && <span>{doc.parties_summary}</span>}
            </div>
          </button>
          );
        })}
      </div>

      {!loading && authenticated && result?.data.documents.length === 0 && (
        <p className="settings-hint">سندی یافت نشد.</p>
      )}
    </div>
  );
}
